'use client';

import type {FC} from 'react';

import {type EmbroideryTextConfiguration} from '@/modules/commerce/embroidery-text/configuration';
import type {EmbroideryTextOptionForSelectorFragment} from '@/modules/commerce/embroidery-text/query';
import {Checkbox} from '@/modules/commerce/form/Checkbox';
import {TextField} from '@/modules/commerce/form/field/TextField';
import {RadioTileFrame} from '@/modules/commerce/form/RadioTileFrame';
import {FormattedItemPrice} from '@/modules/commerce/item/FormattedItemPrice';
import {useDynamicFont} from '@/modules/commerce/product/font/hooks';
import {ProductColorSelector} from '@/modules/commerce/product-color/ProductColorSelector';
import {Spinner} from '@/modules/foundation/components/spinner';
import {Paragraph} from '@/modules/foundation/components/typography/paragraph';
import {cn} from '@/modules/foundation/shared/tailwind';

function FontInput({
	fontId,
	fontUrl,
	fontName,
	namePrefix,
	selectedFontId,
}: {
	fontId: string;
	fontUrl: string;
	fontName: string;
	namePrefix: string;
	selectedFontId?: string;
}) {
	const {isLoaded} = useDynamicFont({name: fontName, url: fontUrl});

	if (!isLoaded) {
		return <Spinner title={'laster...'} />;
	}

	return (
		<RadioTileFrame
			name={`${namePrefix}[fontId]`}
			title={fontName}
			alt={fontName || ''}
			value={`${fontId}`}
			className="sr-only"
			defaultChecked={fontId === selectedFontId}
			labelProps={{
				className: 'bg-preview-background p-2',
			}}
		>
			<div className="flex h-8 min-w-24 flex-row items-center justify-center p-2">
				<div style={{fontFamily: fontName}}>{fontName}</div>
			</div>
		</RadioTileFrame>
	);
}

export type EmbroideryTextSelectorProps = {
	legend?: string;
	namePrefix: string;
	option: EmbroideryTextOptionForSelectorFragment;
	defaultConfiguration?: EmbroideryTextConfiguration;
} & React.ComponentProps<'div'>;

export const EmbroideryTextOptionSelector: FC<EmbroideryTextSelectorProps> = ({
	legend,
	option,
	namePrefix,
	defaultConfiguration,
	className,
	...rest
}) => {
	const selectedFontId = defaultConfiguration?.fontId;
	const selectedColorId = defaultConfiguration?.colorId;
	const text = defaultConfiguration?.text;

	return (
		<div
			className={cn(
				'flex flex-col items-stretch gap-2 rounded-sm border-2 border-neutral-600 bg-neutral-800/50 leading-0 font-semibold select-none',
				'min-h-12 px-3 text-base lg:min-h-14 lg:px-4 lg:text-lg',
				'has-[*>:checked]:border-primary has-[*>:checked]:outline-2 has-[*>:checked]:-outline-offset-4 has-[*>:checked]:outline-neutral-800',
				className,
			)}
			{...rest}
		>
			<Checkbox
				value="enabled"
				name={namePrefix}
				defaultChecked={!!defaultConfiguration}
				labelProps={{
					className: 'flex h-12 lg:h-14',
				}}
			>
				{legend}{' '}
				{option.items && (
					<>
						{' '}
						(+ <FormattedItemPrice items={option.items} />)
					</>
				)}
			</Checkbox>
			{defaultConfiguration && (
				<div className="flex w-full flex-col gap-2 pb-4">
					<fieldset className="mb-3 w-full">
						<Paragraph className="mb-2 font-semibold" asChild>
							<legend>Farge</legend>
						</Paragraph>
						<ProductColorSelector
							namePrefix={`${namePrefix}[colorId]`}
							colors={option.colors ?? []}
							defaultConfiguration={selectedColorId ?? null}
							disableNoneSelection
						/>
					</fieldset>
					<fieldset className="mb-3 w-full">
						<Paragraph className="mb-2 font-semibold" asChild>
							<legend>Skrifttype</legend>
						</Paragraph>
						<div className="flex w-full max-w-full flex-row flex-wrap justify-start gap-2">
							{(option?.collection?.fonts ?? []).map(
								({_id: fontId, name: fontName, file}, i) => {
									const url = file?.url;

									if (!fontName || !url) {
										return null;
									}

									return (
										<FontInput
											key={i}
											fontId={fontId}
											fontName={fontName}
											fontUrl={url}
											namePrefix={namePrefix}
											selectedFontId={selectedFontId}
										/>
									);
								},
							)}
						</div>
					</fieldset>

					<TextField
						type="text"
						label="Tekst"
						name={`${namePrefix}[text]`}
						maxLength={option.maxLength ?? 30}
						defaultValue={text ?? ''}
						placeholder={option.defaultText ?? ''}
						labelProps={{
							className: 'text-base lg:text-lg',
						}}
					/>
				</div>
			)}
		</div>
	);
};
